import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
} from "./useCrudResource"
import {
  getQuotations,
  getQuotation,
  createQuotation,
  editQuotation,
  duplicateQuotation,
  deleteQuotation,
  getAllQuotations,
  getTrashedQuotations,
  restoreQuotation,
  forceDeleteQuotation,
  searchQuotations,
  generateSupportiveQuotations,
  getSupportiveQuotation,
  updateSupportiveQuotation,
  deleteSupportiveQuotation,
  forceDeleteSupportiveQuotation,
  restoreSupportiveQuotation,
  getSupportiveLoan,
  updateSupportiveLoan,
} from "api/quotationApi"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useToastMessage } from "hooks/useToastMessage"

export const QUOTATION_QUERY_KEY = "quotations"
export const SUPPORTIVE_QUOTATION_QUERY_KEY = "supportive-quotations"

// -------------------- FETCH PAGINATED --------------------
export const useQuotations = (params = {}, enabled = true) => {
  return usePaginatedResource(
    QUOTATION_QUERY_KEY,
    getQuotations,
    params,
    enabled
  )
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useQuotation = (id, enabled = true) => {
  return useResourceItem(
    QUOTATION_QUERY_KEY,
    getQuotation,
    id,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateQuotation = () => {
  return useCreateResource(createQuotation, QUOTATION_QUERY_KEY)
}

// -------------------- EDIT --------------------
export const useEditQuotation = () => {
  return useEditResource(editQuotation, QUOTATION_QUERY_KEY)
}

// -------------------- UPDATE STATUS (accept/decline) --------------------
export const useUpdateQuotationStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, quotation_status_id }) =>
      editQuotation({ id, quotation_status_id }),
    onSuccess: () => {
      queryClient.invalidateQueries([QUOTATION_QUERY_KEY])
    },
  })
}

// -------------------- DUPLICATE --------------------
export const useDuplicateQuotation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: duplicateQuotation,
    onSuccess: () => {
      queryClient.invalidateQueries([QUOTATION_QUERY_KEY])
    },
  })
}

// -------------------- DELETE --------------------
export const useDeleteQuotation = () => {
  return useDeleteResource(deleteQuotation, QUOTATION_QUERY_KEY)
}

// -------------------- GENERATE SUPPORTIVE --------------------
export const useGenerateSupportive = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: generateSupportiveQuotations,
    onSuccess: () => {
      queryClient.invalidateQueries([QUOTATION_QUERY_KEY])
    },
  })
}

// -------------------- SUPPORTIVE ITEM --------------------
export const useSupportiveQuotation = (id, enabled = true) => {
  return useQuery({
    queryKey: [SUPPORTIVE_QUOTATION_QUERY_KEY, "view", id],
    queryFn: () => getSupportiveQuotation(id),
    enabled: !!id && enabled,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
  })
}

export const useUpdateSupportiveQuotation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateSupportiveQuotation,
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries([QUOTATION_QUERY_KEY])
      if (variables?.id) {
        queryClient.invalidateQueries([
          SUPPORTIVE_QUOTATION_QUERY_KEY,
          "view",
          variables.id,
        ])
      }
    },
  })
}

export const useSupportiveLoan = (id, enabled = true) => {
  return useQuery({
    queryKey: [SUPPORTIVE_QUOTATION_QUERY_KEY, "loan", id],
    queryFn: () => getSupportiveLoan(id),
    enabled: !!id && enabled,
  })
}

export const useUpdateSupportiveLoan = () => {
  const queryClient = useQueryClient()
  const { handleError } = useToastMessage()

  return useMutation({
    mutationFn: updateSupportiveLoan,
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries([QUOTATION_QUERY_KEY])
      if (variables?.id) {
        queryClient.invalidateQueries([
          SUPPORTIVE_QUOTATION_QUERY_KEY,
          "loan",
          variables.id,
        ])
        queryClient.invalidateQueries([
          SUPPORTIVE_QUOTATION_QUERY_KEY,
          "view",
          variables.id,
        ])
      }
    },
    onError: error => {
      handleError(error, "Failed to save supportive quotation loan information")
    },
  })
}

export const useDeleteSupportiveQuotation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteSupportiveQuotation,
    onSuccess: () => {
      queryClient.invalidateQueries([QUOTATION_QUERY_KEY])
    },
  })
}

export const useForceDeleteSupportiveQuotation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: forceDeleteSupportiveQuotation,
    onSuccess: () => {
      queryClient.invalidateQueries([QUOTATION_QUERY_KEY])
    },
  })
}

export const useRestoreSupportiveQuotation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreSupportiveQuotation,
    onSuccess: () => {
      queryClient.invalidateQueries([QUOTATION_QUERY_KEY])
    },
  })
}

// -------------------- FORCE DELETE --------------------
export const useForceDeleteQuotation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: forceDeleteQuotation,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries([QUOTATION_QUERY_KEY])
    },
  })
}

// -------------------- ADDITIONAL --------------------

// Get all quotations without pagination
export const useAllQuotations = (params = {}, enabled = true) => {
  return usePaginatedResource(
    [QUOTATION_QUERY_KEY, "all"],
    getAllQuotations,
    { ...params, all: true },
    enabled
  )
}

// Get trashed quotations
export const useTrashedQuotations = (params = {}) => {
  return usePaginatedResource(
    QUOTATION_QUERY_KEY,
    getTrashedQuotations,
    params,
    true
  )
}

// Restore a trashed quotation
export const useRestoreQuotation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreQuotation,
    onSuccess: (res, { id }) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      // Update paginated cache
      queryClient.setQueriesData([QUOTATION_QUERY_KEY, "all"], old => {
        if (!old || !old.data || !Array.isArray(old.data.data)) return old
        const updated = structuredClone(old)
        updated.data.data = [restoredItem, ...updated.data.data]
        updated.data.total = (updated.data.total || 0) + 1
        return updated
      })

      // Update individual item cache
      queryClient.setQueryData([QUOTATION_QUERY_KEY, id], restoredItem)
    },
  })
}

// -------------------- SEARCH --------------------
export const useSearchQuotations = (searchTerm, params = {}, enabled = true) => {
  return usePaginatedResource(
    QUOTATION_QUERY_KEY,
    searchQuotations,
    { ...params, search: searchTerm },
    enabled
  )
}
